import { useState } from 'react';

import { useCreateContract, useStartExtraction, useUploadDocument } from '../../api/hooks';
import type { ContractType } from '../../api/types';
import {
  Button,
  FileDropzone,
  FormField,
  Modal,
  Select,
  useToast,
} from '../../components/DesignSystem';
import { errorMessage, isConflict } from '../../lib/errors';
import { fileNameStem, resolveDocumentMimeType } from '../../lib/files';
import { formatFileSize } from '../../lib/format';
import type { ManualReviewReason } from './manualReview';

type Stage = 'idle' | 'uploading' | 'creating' | 'extracting';

const STAGE_LABELS: Record<Exclude<Stage, 'idle'>, string> = {
  uploading: 'Uploading document…',
  creating: 'Creating contract…',
  extracting: 'Extracting terms with AI…',
};

interface StepPrimaryTermsProps {
  onComplete: (
    contractId: string,
    contractType: ContractType,
    manualReviewReason: ManualReviewReason | null,
  ) => void;
}

interface ShareAllocationIssue {
  contractId: string;
  contractType: ContractType;
  message: string;
}

/** Step 1 — contract type, document upload, and AI term extraction. */
export function StepPrimaryTerms({ onComplete }: StepPrimaryTermsProps) {
  const { showToast } = useToast();
  const uploadDocument = useUploadDocument();
  const createContract = useCreateContract();
  const startExtraction = useStartExtraction();

  const [contractType, setContractType] = useState<ContractType>('primary');
  const [file, setFile] = useState<File | null>(null);
  const [stage, setStage] = useState<Stage>('idle');
  const [shareAllocationIssue, setShareAllocationIssue] = useState<ShareAllocationIssue | null>(
    null,
  );

  const isBusy = stage !== 'idle';

  async function handleExtract() {
    if (file === null || isBusy) {
      return;
    }

    try {
      setStage('uploading');
      const document = await uploadDocument.mutateAsync({
        file,
        mimeType: resolveDocumentMimeType(file),
      });

      setStage('creating');
      const contract = await createContract.mutateAsync({
        title: fileNameStem(file.name),
        contractType,
      });

      setStage('extracting');

      try {
        const result = await startExtraction.mutateAsync({
          contractId: contract.id,
          documentId: document.id,
        });
        showToast(
          `Extraction complete — ${result.applied.sharesCreated} shares, ${result.applied.partiesCreated} parties detected`,
        );
        onComplete(contract.id, contractType, null);
      } catch (extractionError) {
        // Recoverable extraction conflicts continue through manual review, with
        // a reason that lets the next step explain what actually happened.
        if (isConflict(extractionError)) {
          const message = errorMessage(extractionError);

          if (/cannot exceed 100/i.test(message)) {
            setShareAllocationIssue({ contractId: contract.id, contractType, message });
            return;
          }

          showToast(message, 'error');
          onComplete(contract.id, contractType, 'ai_unavailable');
          return;
        }

        throw extractionError;
      }
    } catch (error) {
      showToast(errorMessage(error, 'Upload failed — please try again'), 'error');
    } finally {
      setStage('idle');
    }
  }

  return (
    <>
      <div className="wf-upload-grid">
        <section className="rfp-card">
          <div className="rfp-card__head">
            <div>
              <h2 className="rfp-card__title">Contract Metadata Setup</h2>
              <p className="rfp-card__caption">
                Select whether this is a primary distribution contract or a secondary licensing
                contract.
              </p>
            </div>
          </div>
          <FormField htmlFor="contract-type" label="Contract Type">
            <Select
              disabled={isBusy}
              id="contract-type"
              onChange={(event) => setContractType(event.target.value as ContractType)}
              value={contractType}
            >
              <option value="primary">Primary Contract</option>
              <option value="secondary">Secondary Contract</option>
            </Select>
          </FormField>
        </section>

        <section className="rfp-card wf-upload-document">
          <FileDropzone
            accept=".pdf,.doc,.docx"
            hint="Supports PDF, DOC, DOCX files"
            label={file ? file.name : 'Drop your contract here'}
            onFile={setFile}
          />
          {file && (
            <p className="rfp-muted-text" style={{ marginTop: 12 }}>
              Selected: <span className="rfp-mono-text">{file.name}</span> (
              {formatFileSize(file.size)})
            </p>
          )}
          <div className="rfp-wizard-actions">
            <Button disabled={file === null || isBusy} onClick={handleExtract} type="button">
              {isBusy ? STAGE_LABELS[stage] : 'Extract with AI'}{' '}
              {!isBusy && <span aria-hidden="true">→</span>}
            </Button>
          </div>
        </section>
      </div>

      {shareAllocationIssue && (
        <Modal
          actions={
            <Button
              onClick={() => {
                const issue = shareAllocationIssue;
                setShareAllocationIssue(null);
                onComplete(issue.contractId, issue.contractType, 'share_allocation');
              }}
              type="button"
            >
              Review shares <span aria-hidden="true">→</span>
            </Button>
          }
          title="Share allocation needs review"
        >
          <div className="rfp-allocation-issue" role="alert">
            <p className="rfp-allocation-issue__summary">
              The extracted shares allocate more than 100% within the same revenue pool.
            </p>
            <p className="rfp-allocation-issue__detail">{shareAllocationIssue.message}</p>
          </div>
          <p className="rfp-allocation-issue__help">
            Continue to review the affected parties, tiers, and territories before authorising the
            contract.
          </p>
        </Modal>
      )}
    </>
  );
}
